// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import {
  AsyncPipe,
  NgClass,
  NgFor,
  NgIf,
  NgTemplateOutlet,
  UpperCasePipe,
} from '@angular/common';
import { Router, RouterOutlet } from '@angular/router';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { Observable, take } from 'rxjs';
import {
  ModalityModel,
  SystemDetailsModel,
} from '../../../../api/remote-api/.portal.generated/models';
import { SanitizeHtmlPipe } from '../../../shared/pipes/sanitizeHTML.pipe';
import { ScreenLayoutService } from '../../../shared/services/screen-layout-service/screen-layout.service';
import { DeepReadonly } from '../../../shared/utils/api-call-state.util';
import {
  SearchHistoryItem,
  SearchHistoryService,
} from '../../search-history-service/search-history.service';
import { SystemOverviewService } from '../../services/systemOverview.service';
import {
  SearchHistoryHighlighted,
  SearchHistoryItemHighlighted,
} from '../search-pane/search-pane.utils';
import { TranslateModule } from '@ngx-translate/core';

const emptyFieldPlaceholder = '-';
@Component({
  selector: 'app-search-results-list',
  templateUrl: './search-results-list.component.html',
  styleUrls: ['./search-results-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,

  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    NgIf,
    NgFor,
    NgTemplateOutlet,
    NgClass,
    RouterOutlet,
    SanitizeHtmlPipe,
    AsyncPipe,
    UpperCasePipe,
    TranslateModule,
  ],
})
export class SearchResultsListComponent implements OnChanges {
  router = inject(Router);
  @Input({ required: true }) items$!: Observable<SearchHistoryHighlighted>;
  @Input({ required: false }) selectedIndex?: number;
  @Output() openSystem = new EventEmitter<{
    systemSerialNumber: string;
    systemMaterialNumber: number;
  }>();

  currentItem?: SearchHistoryItemHighlighted;
  modalities$ = this.systemOverviewService.modalities.lastSuccessfulData$;

  constructor(
    public systemOverviewService: SystemOverviewService,
    public searchHistoryService: SearchHistoryService,
    public layout: ScreenLayoutService,
    private ref: ElementRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items$']) {
      this.currentItem = undefined;
    }
    if (changes['selectedIndex'] || changes['items$']) {
      if (this.selectedIndex !== undefined) {
        this.items$.pipe(take(1)).subscribe((filteredHistory) => {
          if (this.selectedIndex !== undefined) {
            this.currentItem = filteredHistory[this.selectedIndex];
          }
        });
      } else {
        this.currentItem = undefined;
      }
    }
  }

  async toggleBookmark(item: SearchHistoryItemHighlighted): Promise<void> {
    // TODO: bookmarkItem should take SearchHistoryItem
    await this.searchHistoryService.bookmarkItem(
      item as unknown as SystemDetailsModel,
      !item.bookmark,
    );
  }

  incItem(increment = 1): void {
    this.items$.pipe(take(1)).subscribe((filteredHistory) => {
      if (!this.currentItem) {
        this.currentItem = filteredHistory[0];
      } else {
        let index = filteredHistory.findIndex(
          ({ _key }) => _key === this.currentItem!._key,
        );
        console.log(index);
        if (index !== -1) {
          index =
            (index + filteredHistory.length + increment) %
            filteredHistory.length;
          this.currentItem = filteredHistory[index];
        }
      }
      // if any, scroll item into view
      if (this.currentItem) {
        const el = (this.ref.nativeElement as HTMLElement).querySelector(
          '#item' + this.currentItem._key,
        );
        el?.scrollIntoView();
      }
    });
  }

  highlight(
    item: SearchHistoryItemHighlighted,
    key: keyof SearchHistoryItem,
  ): string {
    const s = String(item[key] ? item[key] : emptyFieldPlaceholder);
    return applyHighlights(s, item.highlights[key]);
  }

  resolveModality(
    modalityId?: number,
    modalities?: DeepReadonly<ModalityModel[]> | null,
  ): string {
    if (modalities && modalityId !== undefined) {
      return modalities.find(({ id }) => id === modalityId)?.displayName || '';
    }
    return '';
  }

  goToSystem({
    systemMaterialNumber,
    systemSerialNumber,
  }: SearchHistoryItemHighlighted): void {
    this.openSystem.emit({ systemMaterialNumber, systemSerialNumber });
  }

  routeToAdvancedSearch(): void {
    this.router.navigateByUrl('/advancedSearch');
  }
}

// could go into a pipe..
// ONLY in case we need this somehwere else!!
function applyHighlights(
  str: string,
  highlights: { start: number; end: number }[],
  left = `<span style="background: rgba(var(--functional-yellow), var(--opacity-1));">`,
  right = `</span>`,
): string {
  const sorted = [...(highlights ?? [])];
  sorted.sort((a, b) => b.start - a.start);
  const indices: number[] = [];
  sorted.forEach(({ start, end }) => {
    indices.push(start);
    indices.push(end);
  });
  if (indices.length === 0) {
    // return unhighlighted string in case no matches were found
    return str;
  }
  const fragments: string[] = [];
  for (let i = 0; i < indices.length; i += 2) {
    fragments.push(str.substring(i > 0 ? indices[i - 1] : 0, indices[i]));
    fragments.push(left);
    fragments.push(str.substring(indices[i], indices[i + 1]));
    fragments.push(right);
    if (i === indices.length - 2) {
      fragments.push(str.substring(indices[i + 1], str.length));
    }
  }
  return fragments.join('');
}
